'use client';
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import ButtonsPages from './legacy_blocks/ButtonsPages';
import Typography from '@egalite/ui/Typography';
import UploadButton from '@egalite/ui/Form/UploadButton';
import { Check } from '@egalite/ui/Form';
import ErrorList from '@egalite/ui/ErrorList';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/candidate/disabilityDataUpload';

import { useState, useEffect, FC } from 'react';
import PrimaryButton from '@egalite/ui/Buttons';
import AttachedFileBlock from '../CompanyCandidateProfile/legacy_blocks/AttachedFileBlock';

import get from 'lodash/get';
import split from 'lodash/split';
import last from 'lodash/last';
import truncate from 'lodash/truncate';
import pick from 'lodash/pick';
import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Button from '@egalite/ui/Buttons';
import save from './save';
import { set } from 'lodash';
import FormPage from './legacy_blocks/CandidateFormPage';
import Loading from '@egalite/ui/Loading';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import authFetch from '../../lib/auth/authFetch';
import Cookies from 'js-cookie';
import useSomeChangedEffect from './useSomeChangedEffect';

type ReportFile = {
	name: string;
	link: string;
	key: string;
};

const getFileName = file => {
	if (file == null) return null;
	if (file == '') return '';

	const ext = last(split(file, '.'));
	const name = last(split(file, '/')).replace(`.${ext}`, '');
	return `${truncate(name, { length: 20 })}${ext ? `.${ext}` : ''}`;
};

const solveUploadingList = (formValues, uploadingState, uploaded) => {
	const valArr:ReportFile[]  = Array.from(formValues ?? []);
	const uppingArr:ReportFile[]  = Array.from(uploadingState ?? []);
	const uppedArr:ReportFile[]  = Array.from(uploaded ?? []);

	const newUploadingList:ReportFile[] = [...valArr, ...uppingArr]
		.filter((f, i, array) => array.findIndex(l => l.name === f.name) === i)
		.filter(g => !uppedArr.map(u => u.name).includes(g.name));
	return newUploadingList;
};

const DisabilityDataUploadForm: FC<{edit?: boolean, onSuccess?: () => void}> = ({ edit = false, onSuccess = null }) => {
	const router = useRouter();
	const notification = useNotification();
	const [loadingData, setLoadingData] = useState(true);
	const [candidateData, setCandidateData] = useState<{ reportFiles?: ReportFile[], report: any; doesntHaveReport: boolean; }>(initialValues);
	const [reportsUpload, setReportsUpload] = useState([]);
	const [reportsUploading, setReportsUploading] = useState([]);

	const [formController, getFieldProps, { loading, submitting, success, errors, data }] = useForm({
		initialValues: initialValues,
		validationSchema: null, // Remover validação para permitir salvar sem arquivo
		mutation: async data => {
			const token = Cookies.get('token')
			const fd = new FormData();
			// Verificar se há arquivos para decidir estratégia
			const hasFiles = data.report && 
				typeof data.report === 'object' && 
				data.report instanceof FileList && 
				(data.report as FileList).length > 0;
			
			Object.entries(data).forEach(([key, value]) => {
				// Pular valores nulos/undefined
				if (value == null) return;
				
				// Para o campo 'report', só enviar se há arquivos
				if (key === 'report') {
					if (hasFiles && value instanceof FileList) {
						for(let i = 0; i < (value as FileList).length; i++) {
							fd.append(key, (value as FileList)[i]);
						}
					}
					return;
				}
				
				// Outros campos
				if (typeof value === 'string') {
					fd.append(key, value);
				} else if (typeof value === 'boolean' || typeof value === 'number') {
					fd.append(key, String(value));
				} else if (typeof value === 'object') {
					fd.append(key, JSON.stringify(value));
				}
			});
			
			// Se não há arquivos, indicar que usuário optou por não enviar
			if (!hasFiles) {
				fd.append('doesntHaveReport', 'true');
			}
			return await fetch('/api/v1/candidate/disabilityDataUpload', {
                method: 'post', 
                body: fd, 
                headers: {
                    'Authorization': token,
                },
                mode: 'cors',
                cache: 'no-cache',
                credentials: 'same-origin',
                redirect: 'follow',
            }).then(res => res.json());
		},
		onSuccess: (newData) => {
			setCandidateData(newData || data);
			if (onSuccess != null) {
				onSuccess();
			}
		}
	});

	const deleteReport = useCallback(
		key => {
			save('/api/v1/candidate/disabilityDataUploadDelete', { report: key })
				.then(res => {
					if (res.success) {
						candidateData.reportFiles = res.data.reportFiles;
						setCandidateData({ ...candidateData, reportFiles: res.data.reportFiles });
					} else {
						console.error('Erro ao deletar arquivo', res);
					}
				})
				.catch(error => {
					console.error(error);
				});
		},
		[candidateData, candidateData.reportFiles],
	);

	useEffect(() => {
		if (loadingData) {
			authFetch('/api/v2/candidate/profile', Cookies.get('token'))
				.then(res => res.json())
				.then(data => {
					if (data.success === true) {
						setCandidateData(data.data);
						setLoadingData(false);
					} else {
						notification.error('Erro ao carregar dados do candidato');
					}
				});
		}
	}, [formController, router, loadingData, notification]);

	useSomeChangedEffect(() => {
		const reportList: ReportFile[] = formController.values.report;
		if (reportList && Array.from(reportList).some(r => !!r.name)) {
			setReportsUploading(solveUploadingList(reportList, reportsUploading, candidateData?.reportFiles));
			formController.submitForm();
		}
	}, [candidateData?.reportFiles, formController, formController.values.report, reportsUploading], [formController.values.report]);

	useSomeChangedEffect(() => {
		if (candidateData) {
			setReportsUploading(solveUploadingList(formController.values.report, reportsUploading, candidateData?.reportFiles));
			setReportsUpload(candidateData?.reportFiles ?? []);
		}
	}, [candidateData, candidateData.reportFiles, formController.values.report, reportsUploading], [candidateData, candidateData.reportFiles]);

	if (loadingData) {
		return <Loading fullScreen={true}>carregando dados</Loading>;
	}

	return (
		<FormPage
			actualStep={4}
			formActualStep={2}
			formTotalSteps={2}
			progress={90}
			formTitle={'Dados da Deficiência'}
			// previousForm="/"
			showStepsMenu={false}
			// showBackButton={false}
			modal={edit}
		>
			<form onSubmit={formController.handleSubmit}>
				<div className="mb-16 md:mb-24">
					{edit ? <Typography variant="headline-5" className="text-brand-main mb-16">
						Laudo médico
					</Typography> : null}
					<Typography variant="body2" component="h6" className='mb-12'>
						Você tem laudo médico de deficiência?
					</Typography>
					<UploadButton id="report" multiple={true} labelFilesToUpload={'Arquivos prontos para upload'} {...getFieldProps('report')}>
						Anexar laudo médico
					</UploadButton>

					<div className='mt-12'>
						{reportsUploading.map((u, index) => (
							<div key={`upng${index}`} css={{ marginTop: 8, marginBottom: 8 }}>
								<AttachedFileBlock fileName={getFileName(u.name)} loading={true} className={'border border-solid border-base-brand-light'} />
							</div>
						))}
						{reportsUpload.map((u, index) => (
							<div key={`upd${index}`} css={{ marginTop: 8, marginBottom: 8 }}>
								<AttachedFileBlock
									fileName={getFileName(u.name)}
									fileLink={u.link}
									deletable={true}
									onDelete={() => {
										deleteReport(u.key);
									}}
									className={'border border-solid border-base-brand-light'}
								/>
							</div>
						))}
					</div>

					<ErrorList errors={formController.errors} touched={formController.touched} />

					{errors != null &&
						errors.map((err, index) => (
							<Typography key={`err-${index}`} variant="body2" color="error.dark">
								{err}
							</Typography>
						))}
				</div>
				<div className='md:mt-24 mt-16'>
					{edit ?
						<Button type='submit' variant='primary' className='w-full'>
							{submitting ? 'Salvando...' : 'Salvar'}
						</Button>
						:
						<>
							<Button variant='secondary' type='link' href='/candidato/form/disabilityDataIndex' className='mr-4'>Voltar</Button>
							<Button
								variant="primary"
								type="link"
								href="/candidato/form/complete"
							>
								{candidateData?.reportFiles?.length ? 'Continuar' : 'Continuar sem laudo'}
							</Button>
						</>
					}
				</div>
			</form>
		</FormPage>
	);
};

export default DisabilityDataUploadForm;
